// ============================================================
// AudienceDialog - who gets to see it (v8.363.10 as the handouts' reveal
// dialog; generic since v9.5.11, when a clock gained an audience too).
//
// The table, every player, a vox group (its members NOW - a snapshot,
// which the dialog says), named people, or nobody. With `announce` it
// also offers "Announce in the vox" (the handouts' line with the
// thumbnail). The answer is a `RevealTarget`; `audienceForReveal`
// (engine/handouts/reveal) turns it into the audience column.
// ============================================================
import { useState } from 'react';
import common from '../styles/common.module.css';
import pstyles from '../styles/Permissions.module.css';
import clsx from '../utils/clsx';
import useFocusTrap from '../hooks/useFocusTrap.js';
import useModalBackdrop from '../hooks/useModalBackdrop';
import useEscapeKey from '../hooks/useEscapeKey.js';
import type { ChatChannel, Person } from '../engine/chat';
import type { RevealTarget } from '../engine/handouts';
import { warnIfRejected } from '../diagnostics/log';

interface Props {
  show: boolean;
  /** What is being shown, for the heading and the dialog's name. */
  title: string;
  people: Person[];
  channels: ChatChannel[];
  /** Offer "Announce in the vox" (the handouts'); a clock has nothing to announce. */
  announce?: boolean;
  /** The words for nobody: a handout goes back to a draft, a clock is the GM's alone. */
  nobodyLabel?: string;
  /** The diagnostic tag a rejected write is filed under. */
  tag?: string;
  onPick: (target: RevealTarget, announce: boolean) => Promise<unknown> | unknown;
  /** Offer "Show and present" (the handouts', v9.8.0): the same pick, and it opens on their screens. */
  onPresent?: (target: RevealTarget, announce: boolean) => Promise<unknown> | unknown;
  onClose: () => void;
}

type Mode = 'table' | 'players' | 'channel' | 'people' | 'draft';
type Choosing = { mode: Mode; setMode: (mode: Mode) => void };
type OptionProps = { value: Mode; label: string; testId: string; choosing: Choosing };

/**
 * One audience radio. Module-level on purpose: a component defined inside
 * a render is a new type every render, so React remounted every radio on
 * each change and keyboard focus was lost (v8.377.5).
 */
function AudienceOption({ value, label, testId, choosing }: OptionProps) {
  return (
    <label className={common.label} style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
      <input
        type="radio"
        name="reveal"
        checked={choosing.mode === value}
        onChange={() => choosing.setMode(value)}
        data-testid={testId}
      />
      {label}
    </label>
  );
}

/** What the choice reveals to, and whether it names anyone yet. The first group stands in until one is picked. */
function revealChoice(mode: Mode, channelId: string, channels: ChatChannel[], picked: string[]) {
  if (mode === 'channel') {
    return {
      target: { kind: 'channel', id: channelId || channels[0]?.id || '' } as RevealTarget,
      valid: !!(channelId || channels[0]),
    };
  }
  if (mode === 'people') return { target: { kind: 'people', uids: picked } as RevealTarget, valid: picked.length > 0 };
  return { target: { kind: mode } as RevealTarget, valid: true };
}

function ChannelChoice({
  choosing,
  channels,
  channelId,
  onPick,
}: {
  choosing: Choosing;
  channels: ChatChannel[];
  channelId: string;
  onPick: (id: string) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <AudienceOption value="channel" label="A vox group" testId="reveal-channel" choosing={choosing} />
      <select
        className={common.input}
        value={channelId || channels[0]?.id}
        onChange={(e) => onPick(e.target.value)}
        aria-label="Vox group"
        data-testid="reveal-channel-select"
      >
        {channels.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
    </div>
  );
}

function PeopleChoice({
  choosing,
  players,
  picked,
  onToggle,
}: {
  choosing: Choosing;
  players: Person[];
  picked: string[];
  onToggle: (uid: string, on: boolean) => void;
}) {
  return (
    <>
      <AudienceOption value="people" label="These people" testId="reveal-people" choosing={choosing} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginLeft: 22 }}>
        {players.map((p) => (
          <label key={p.uid} className={common.label} style={{ flexDirection: 'row', gap: 4, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={picked.includes(p.uid)}
              onChange={(e) => onToggle(p.uid, e.target.checked)}
              aria-label={p.name}
              data-testid={`reveal-person-${p.uid}`}
            />
            {p.actingAsName ? `${p.name} (as ${p.actingAsName})` : p.name}
          </label>
        ))}
      </div>
    </>
  );
}

function AnnounceBox({
  announce,
  disabled,
  onChange,
}: {
  announce: boolean;
  disabled: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <label className={common.label} style={{ flexDirection: 'row', gap: 6, alignItems: 'center', marginTop: 10 }}>
      <input
        type="checkbox"
        checked={announce}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid="reveal-announce"
      />
      Announce in the vox
    </label>
  );
}

function DialogFooter({
  hide,
  valid,
  onCancel,
  onConfirm,
  onPresent,
}: {
  hide: boolean;
  valid: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onPresent?: () => void;
}) {
  return (
    <div className={pstyles.footer}>
      <button type="button" className={common.btnSecondary} onClick={onCancel}>
        Cancel
      </button>
      {onPresent && !hide && (
        <button
          type="button"
          className={common.btnSecondary}
          disabled={!valid}
          onClick={onPresent}
          title="Show it, and open it on their screens"
          data-testid="reveal-present"
        >
          Show and present
        </button>
      )}
      <button
        type="button"
        className={common.btnPrimary}
        disabled={!valid}
        onClick={onConfirm}
        data-testid="reveal-confirm"
      >
        {hide ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

/** The dialog's choice: a mode, the group or people it names, and whether to announce. */
function useRevealState() {
  const [mode, setMode] = useState<Mode>('table');
  const [channelId, setChannelId] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [announce, setAnnounce] = useState(true);
  // Picking a group or a person picks that mode too.
  const pickChannel = (id: string) => {
    setChannelId(id);
    setMode('channel');
  };
  const togglePerson = (uid: string, on: boolean) => {
    setPicked((cur) => (on ? [...cur, uid] : cur.filter((u) => u !== uid)));
    setMode('people');
  };
  return { mode, setMode, channelId, pickChannel, picked, togglePerson, announce, setAnnounce };
}

type RevealState = ReturnType<typeof useRevealState>;

/** Every audience on offer: the table, the players, a group, named people, or nobody. */
function Audiences({
  st,
  players,
  channels,
  nobodyLabel,
}: {
  st: RevealState;
  players: Person[];
  channels: ChatChannel[];
  nobodyLabel: string;
}) {
  const choosing = { mode: st.mode, setMode: st.setMode };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <AudienceOption value="table" label="The table - everyone" testId="reveal-table" choosing={choosing} />
      <AudienceOption
        value="players"
        label="Every player, not the GM's console"
        testId="reveal-players"
        choosing={choosing}
      />
      {channels.length > 0 && (
        <ChannelChoice choosing={choosing} channels={channels} channelId={st.channelId} onPick={st.pickChannel} />
      )}
      {players.length > 0 && (
        <PeopleChoice choosing={choosing} players={players} picked={st.picked} onToggle={st.togglePerson} />
      )}
      <AudienceOption value="draft" label={nobodyLabel} testId="reveal-draft" choosing={choosing} />
    </div>
  );
}

export default function AudienceDialog({
  show,
  title,
  people,
  channels,
  announce = false,
  nobodyLabel = 'Nobody - back to a draft',
  tag = 'story',
  onPick,
  onPresent,
  onClose,
}: Props) {
  const trapRef = useFocusTrap(show);
  const backdrop = useModalBackdrop(onClose, trapRef);
  useEscapeKey(show, onClose);
  const st = useRevealState();
  if (!show) return null;
  const { mode } = st;
  const { target, valid } = revealChoice(mode, st.channelId, channels, st.picked);
  // The pick goes to `send` (Show, or Show and present), then the dialog closes.
  const confirm = async (send: NonNullable<Props['onPresent']>) => {
    if (!valid) return;
    await send(target, announce && mode !== 'draft' && st.announce);
    onClose();
  };
  return (
    <div className={common.modal} onClick={backdrop.onBackdropClick} role="presentation">
      <div
        ref={backdrop.boxRef}
        className={clsx(common.modalContent, pstyles.content)}
        role="dialog"
        aria-modal="true"
        aria-label={`Show ${title}`}
        data-testid="reveal-dialog"
      >
        <h3 style={{ marginTop: 0 }}>Show “{title}”</h3>
        <Audiences st={st} players={people.filter((p) => !p.isGm)} channels={channels} nobodyLabel={nobodyLabel} />
        {mode === 'channel' && (
          <p className={pstyles.hint}>The group's members as they are now. Somebody who joins later is not shown it.</p>
        )}
        {announce && <AnnounceBox announce={st.announce} disabled={mode === 'draft'} onChange={st.setAnnounce} />}
        <DialogFooter
          hide={mode === 'draft'}
          valid={valid}
          onCancel={onClose}
          onConfirm={() => warnIfRejected(confirm(onPick), tag, 'reveal')}
          onPresent={onPresent && (() => warnIfRejected(confirm(onPresent), tag, 'present'))}
        />
      </div>
    </div>
  );
}
